import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import permissionService from '../../services/permissionService';
import AppDataGrid from '../common/AppDataGrid';
import GridActionsCell from '../common/GridActionsCell';
import TableSearch from '../common/TableSearch';
import { useDataTable } from '../../hooks/useDataTable';

const PermissionsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);

  // Page, size, search and sort live in the URL (/permissions?page=0&size=20&search=...).
  const table = useDataTable(permissionService.getPermissionsPage, {}, {
    onError: () => toast.error(t('permissions.fetchError')),
  });
  const fetchPermissions = table.refresh;

  const handleDelete = async () => {
    try {
      await permissionService.deletePermission(permissionToDelete.id);
      toast.success(t('permissions.deleteSuccess'));
      fetchPermissions();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const columns = [
    { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('common.name'), width: 200 },
    { field: 'displayName', headerName: t('common.displayName'), width: 200 },
    { field: 'description', headerName: t('common.description'), flex: 1 },
    {
      field: 'resource',
      headerName: t('permissions.resource'),
      width: 120,
      cellRenderer: (params) => (
        <Chip label={params.value} color="primary" size="small" />
      )
    },
    {
      field: 'action',
      headerName: t('permissions.action'),
      width: 120,
      cellRenderer: (params) => (
        <Chip label={params.value} color="secondary" size="small" />
      )
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 110,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: ViewIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/permissions/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/permissions/edit/${params.data.id}`),
            },
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              onClick: () => {
                setPermissionToDelete(params.data);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('permissions.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/permissions/create')}
        >
          {t('permissions.createPermission')}
        </Button>
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <TableSearch
          placeholder={t('common.search')}
          value={table.searchInput}
          onChange={table.handleSearchChange}
        />
      </Card>

      <Card>
        <AppDataGrid
          rows={table.data.content}
          columns={columns}
          loading={table.loading}
          paginationMode="server"
          rowCount={table.data.totalElements}
          paginationModel={table.paginationModel}
          onPaginationModelChange={table.handlePaginationModelChange}
          sortModel={table.sortModel}
          onSortModelChange={table.handleSortChange}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('permissions.deletePermission')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.deleteConfirmText', { name: permissionToDelete?.displayName })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionsList;
